import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Layout, Button, Modal, Tooltip } from "antd";
import { ReloadOutlined } from '@ant-design/icons';

import appActions from "@stores/reducers/app/actions";
import { fetchShowSupportTicket } from "@stores/reducers/menu.reducer";
import TopbarNotification from "./TopbarNotification";
import TopbarUser from "./TopbarUser";
import TopbarConfig from "./TopbarConfig";
import TopbarWrapper from "./Topbar.styles";
import useDesktopShortcuts from "../../../hooks/useDesktopShortcuts";

import { APP_VERSION } from "../../../version";
import { useVersionCheck } from "../../../hooks/useVersionCheck";
import { getIcon } from "./topbarIconMap";

const { Header } = Layout;
const { toggleCollapsed } = appActions;

const environment = process.env.REACT_APP_SPARTACUS_ENVIRONMENT;
const environmentShort = process.env.REACT_APP_SPARTACUS_ENVIRONMENT_SHORT_NAME;
const SUPPORT_TICKET_URL = 'https://suporte.grupotrapezio.com.br/ticket/create';


export default function Topbar() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { collapsed, openDrawer } = useSelector((state) => state.App);
  const isCollapsed = collapsed && !openDrawer;

  const { showSupportTicket, showSupportTicketLoaded } = useSelector((state) => state.menu);

  useEffect(() => {
    if (!showSupportTicketLoaded) {
      dispatch(fetchShowSupportTicket());
    }
  }, [dispatch, showSupportTicketLoaded]);

  const { isOutdated, latestVersion } = useVersionCheck();
  const {
    availableScreens,
    shortcuts,
    selectedKeys,
    toggleShortcut,
    saveShortcuts,
    maxShortcuts,
  } = useDesktopShortcuts();

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState("");

  useEffect(() => {
    if (isOutdated) {
      setModalVisible(true);
    }
  }, [isOutdated]);

  const handleToggle = React.useCallback(() => {
    dispatch(toggleCollapsed());
  }, [dispatch]);

  const styling = {
    background: '#ffffff',
    position: "fixed",
    width: "100%",
    height: 70,
  };

  const handleUpdate = () => {
    if ('caches' in window) {
      caches.keys().then((names) => {
        for (let name of names) {
          caches.delete(name);
        }
      });
    }

    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((reg) => reg.unregister());
      });
    }

    window.location.reload(true);
  };

  return (
    <TopbarWrapper>
      <Header
        style={styling}
        className={isCollapsed ? "isomorphicTopbar collapsed" : "isomorphicTopbar"}
      >
        <div className="isoLeft">
          <button
            className={isCollapsed ? "triggerBtn menuCollapsed" : "triggerBtn menuOpen"}
            style={{ color: '#0f172a' }}
            onClick={handleToggle}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 24 }}>
            {showSupportTicket && (
              <Tooltip title="Abrir Chamado" placement="bottom">
                <a
                  href={SUPPORT_TICKET_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#334155',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    height: 34,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#e2e8f0';
                    e.currentTarget.style.color = '#0f172a';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.color = '#334155';
                  }}
                >
                  <i className="ion-chatbubble-working" style={{ fontSize: 14 }} />
                  Abrir Chamado
                </a>
              </Tooltip>
            )}
            <Tooltip title="Configurar atalhos e empresa" placement="bottom">
              <div>
                <TopbarConfig
                  availableScreens={availableScreens}
                  selectedKeys={selectedKeys}
                  toggleShortcut={toggleShortcut}
                  saveShortcuts={saveShortcuts}
                  maxShortcuts={maxShortcuts}
                />
              </div>
            </Tooltip>
            {shortcuts.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: '0 4px 0 10px',
                  height: 34,
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    marginRight: 8,
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                  }}
                >
                  Acesso Rápido
                </span>
                <div style={{ width: 1, height: 18, background: '#cbd5e1', flexShrink: 0 }} />
                {shortcuts.map(sc => {
                  const Icon = getIcon(sc.icon);
                  return (
                    <Tooltip key={sc.key} title={sc.label} placement="bottom">
                      <button
                        onClick={() => history.push(sc.path)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          borderRadius: 6,
                          padding: '4px 10px',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#334155',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.15s',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          lineHeight: 1,
                          height: 26,
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = '#e2e8f0';
                          e.currentTarget.style.color = '#0f172a';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#334155';
                        }}
                      >
                        <Icon size={13} style={{ flexShrink: 0, opacity: 0.7 }} />
                        {sc.label}
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <ul className="isoRight">
          <li>
            <span
              style={{
                backgroundColor: '#f1f5f9',
                color: '#94a3b8',
                fontSize: 11,
                fontWeight: 600,
                padding: '4px 12px',
                borderRadius: 12,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                whiteSpace: 'nowrap',
                border: '1px solid #e2e8f0',
              }}
            >
              {environment && environment !== 'production' && environmentShort && (
                <span
                  style={{
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    letterSpacing: '0.03em',
                  }}
                >
                  {environmentShort}
                </span>
              )}
              {APP_VERSION}
            </span>
          </li>
          <li
            onClick={() => setSelectedItem("notification")}
            className={selectedItem === "notification" ? "isoNotify active" : "isoNotify"}
          >
            <TopbarNotification />
          </li>
          <li
            onClick={() => setSelectedItem("user")}
            className="isoUser"
          >
            <TopbarUser />
          </li>
        </ul>
      </Header>

      <Modal
        title="Atualização obrigatória"
        open={modalVisible}
        footer={[
          <Button
            key="update"
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleUpdate}
          >
            Atualizar
          </Button>,
        ]}
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
      >
        <p>Uma nova versão <strong>({latestVersion})</strong> está disponível.</p>
        <p>Você precisa atualizar para continuar utilizando o sistema.</p>
      </Modal>
    </TopbarWrapper>
  );
}
